import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../../utils/config';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Target,
  Loader2,
  Plus,
  X,
  AlertCircle,
  CheckCircle2,
  Info,
} from 'lucide-react';

const SetGoalModal = ({
  open,
  onOpenChange,
  teamPageId,
  currentGoal = 0,
  currentMilestones = [],
  onSuccess,
}) => {
  const { token } = useAuth();
  const [goalAmount, setGoalAmount] = useState(currentGoal || '');
  const [milestones, setMilestones] = useState([]);
  const [newMilestone, setNewMilestone] = useState('');
  const [useCustomMilestones, setUseCustomMilestones] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      setGoalAmount(currentGoal || '');
      setMilestones(currentMilestones.filter(m => m !== currentGoal) || []);
      setUseCustomMilestones(currentMilestones.length > 0 && currentMilestones.length !== 4);
      setError('');
      setSuccess(false);
    }
  }, [open, currentGoal, currentMilestones]);

  const generateDefaultMilestones = (goal) => {
    if (!goal || goal < 50) return [];
    const percentages = [0.25, 0.50, 0.75];
    return percentages.map(p => Math.round(goal * p));
  };

  const handleAddMilestone = () => {
    const value = parseFloat(newMilestone);
    if (!value || value <= 0) {
      setError('Please enter a valid milestone amount');
      return;
    }
    if (value >= parseFloat(goalAmount)) {
      setError('Milestone must be less than goal amount');
      return;
    }
    if (milestones.includes(value)) {
      setError('This milestone already exists');
      return;
    }
    setMilestones([...milestones, value].sort((a, b) => a - b));
    setNewMilestone('');
    setError('');
  };

  const handleRemoveMilestone = (value) => {
    setMilestones(milestones.filter(m => m !== value));
  };

  const handleSubmit = async () => {
    const goal = parseFloat(goalAmount);
    
    if (!goal || goal < 50) {
      setError('Goal must be at least $50');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const milestoneSteps = useCustomMilestones 
        ? [...milestones, goal].sort((a, b) => a - b)
        : null; // Will use auto-generated milestones

      const response = await fetch(`${BACKEND_URL}/api/team-pages/${teamPageId}/set-goal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          goal_amount: goal,
          milestone_steps: milestoneSteps,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to set goal');
      }

      setSuccess(true);
      setTimeout(() => {
        onOpenChange(false);
        if (onSuccess) onSuccess(data);
      }, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const previewMilestones = useCustomMilestones
    ? milestones
    : generateDefaultMilestones(parseFloat(goalAmount) || 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-card border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-emerald-500" />
            Set Fundraising Goal
          </DialogTitle>
          <DialogDescription>
            Set your goal and track progress with milestones
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Error/Success Messages */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-500 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          {success && (
            <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-emerald-500 text-sm">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              Goal updated successfully!
            </div>
          )}

          {/* Goal Amount */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Goal Amount *
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
              <Input
                type="number"
                placeholder="1000"
                value={goalAmount}
                onChange={(e) => setGoalAmount(e.target.value)}
                className="pl-8"
                min={50}
                max={10000000}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Minimum goal: $50
            </p>
          </div>

          {/* Milestone Toggle */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">Custom Milestones</span>
            <button
              type="button"
              onClick={() => setUseCustomMilestones(!useCustomMilestones)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                useCustomMilestones ? 'bg-emerald-500' : 'bg-secondary'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  useCustomMilestones ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Custom Milestones Input */}
          {useCustomMilestones && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Add Milestones
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    type="number"
                    placeholder="250"
                    value={newMilestone}
                    onChange={(e) => setNewMilestone(e.target.value)}
                    className="pl-8"
                    onKeyPress={(e) => e.key === 'Enter' && handleAddMilestone()}
                  />
                </div>
                <Button type="button" onClick={handleAddMilestone} variant="outline">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {/* Milestone Tags */}
              {milestones.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {milestones.map((m) => (
                    <span
                      key={m}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-secondary rounded-lg text-sm"
                    >
                      ${m.toLocaleString()}
                      <button
                        type="button"
                        onClick={() => handleRemoveMilestone(m)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Milestone Preview */}
          {parseFloat(goalAmount) >= 50 && (
            <div className="bg-secondary/50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Info className="w-4 h-4 text-emerald-500" />
                <span className="text-sm font-medium text-foreground">Milestone Preview</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {previewMilestones.map((m, i) => (
                  <span
                    key={i}
                    className="px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded text-xs"
                  >
                    ${m.toLocaleString()} ({Math.round((m / parseFloat(goalAmount)) * 100)}%)
                  </span>
                ))}
                <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs font-medium">
                  ${parseFloat(goalAmount).toLocaleString()} (Goal)
                </span>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={loading || !goalAmount || parseFloat(goalAmount) < 50}
            className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...
              </>
            ) : (
              <>
                <Target className="w-4 h-4 mr-2" /> Set Goal
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SetGoalModal;
